import React, { useState, useRef } from 'react';
import {
  Button, Form, Input, TreeSelect, Card, Divider, Typography, Select, Space, Tooltip
} from 'antd';
import {
  MinusCircleOutlined, PlusOutlined, ExperimentOutlined, CheckOutlined, StopOutlined
} from '@ant-design/icons';
import lodash from 'lodash';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const { Title, Text } = Typography;
const { Option } = Select;

const CustomizedForm = ({
  onChange, infields, onFinish, themes, subThemes, setShow
}) => {
  const [form] = Form.useForm();

  const endRef = useRef(null);
  const globalFields = infields;

  const getThemesTreeData = () => {
    const optionsThemes = themes.map(theme => (
      {
        label: `${theme.ref} ${theme.name}`,
        value: theme.id,
      }
    ));
    return lodash.sortBy(optionsThemes, ['label'], ['desc']);
  };

  const getSubThemeOptions = (themeId) => {
    const subthemes = subThemes.filter(subtheme => subtheme.parentId === themeId);
    const subThemesOptions = subthemes.map(option => (
      {
        label: `${option.ref} ${option.title}`,
        value: option.id,
      }
    ));
    return lodash.sortBy(subThemesOptions, ['label'], ['desc']);
  };

  const getSubThemeLetters = (subThemeId) => {
    const subTheme = subThemes.find(theme => theme.id === subThemeId);
    const { letters } = subTheme || [];
    return letters;
  };

  const getSubThemeTreeData = (subThemeId) => {
    const orderedSubThemes = lodash.sortBy(subThemes, ['ref'], ['desc']);
    return orderedSubThemes.reduce((acc, obj) => {
      if (obj.parentId === subThemeId) {
        acc.push({
          label: `${obj.ref} ${obj.title}`,
          value: obj.id,
          children: orderedSubThemes.reduce((a, b) => {
            if (b.parentId === obj.id) {
              a.push({
                label: `${b.ref} ${b.title}`,
                value: b.id,
                children: orderedSubThemes.reduce((c, d) => {
                  if (d.parentId === b.id) {
                    c.push({
                      label: `${d.ref} ${d.title}`,
                      value: d.id,
                    });
                  }
                  return c;
                }, [])
              });
            }
            return a;
          }, [])
        });
      }
      return acc;
    }, []);
  };

  const selectableTree = (tree) => tree.map(item => {
    if (item.children && !lodash.isEmpty(item.children)) {
      item.value = `toplevel${item.value}`;
      item.children.map(child => {
        if (child.children && !lodash.isEmpty(child.children)) {
          child.value = `toplevel${child.value}`;
          child.children.map(subChild => {
            if (subChild.children && !lodash.isEmpty(subChild.children)) {
              subChild.value = `toplevel${subChild.value}`;
            }
            return subChild;
          });
        }
        return child;
      });
    }
    return item;
  });

  /**
   * Check if ph input needed
   * @param indice
   * @returns {boolean}
   */
  const checkForInputPhNeeded = (item) => {
    let showPhInput = false;
    if (item && !lodash.isEmpty(item.items)) {
      item.items.forEach((n) => {
        if (n.letters.code === 'B' && n.letters.condition === 'X(*)') {
          showPhInput = !showPhInput;
        }
      });
    }
    return showPhInput;
  };

  /**
   * Check if bold touch select needed
   * @param item
   * @returns {boolean}
   */
  const checkForBoldTouch = (item) => {
    let showBoldTouchInput = false;
    if (item && !lodash.isEmpty(item.items)) {
      item.items.forEach((n) => {
        if (n.letters.code === 'D2' && n.letters.condition.includes('**')) {
          showBoldTouchInput = !showBoldTouchInput;
        }
      });
    }
    return showBoldTouchInput;
  };

  function getLettersCode(letters) {
    if (!letters) return '';
    const { items } = letters;
    if (!items[0]?.letters?.code) return '';
    return (
      <Tooltip placement="topLeft" title="Le code peut évolué en fonction de la recette globale">
        <Text type="success">{`Code simulant estimé: ${items[0]?.letters?.code}`}</Text>
      </Tooltip>
    );
  }

  return (
    <>
      <Divider>
        <Title level={2}>
          <ExperimentOutlined />
          {' '}
          Recette
        </Title>
      </Divider>
      <Form
        form={form}
        name="global_state"
        onFinish={onFinish}
        layout="vertical"
        infields={infields}
        onFieldsChange={(_, allFields) => {
          onChange(allFields);
        }}
      >
        <Form.Item
          name="name"
          label="Nom de la recette"
          placeholder="Recette"
          rules={[
            {
              required: true,
              message: 'Champs requis',
            },
          ]}
        >
          <Input size="large" />
        </Form.Item>
        <InnerFormWrapper>
          <Form.List
            name="ingredients"
            rules={[
              {
                // eslint-disable-next-line consistent-return
                validator: async (_, names) => {
                  if (lodash.isUndefined(names) || lodash.isEmpty(names)) {
                    return Promise.reject(new Error('Ajouter au moins un ingrédient'));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({
                  key, name, fieldKey, ...restField
                }) => {
                  let letters = null;
                  let subThemesOptions = null;
                  let themeValue = null;
                  let subThemeValue = null;
                  let lastTreeValue = null;
                  let lastTreeOptions = null;

                  themeValue = globalFields.find(
                    field => field.name.includes(name)
                      && field.name.includes('theme')
                  );

                  subThemeValue = globalFields.find(
                    field => field.name.includes(name)
                      && field.name.includes('subtheme')
                  );

                  lastTreeValue = globalFields.find(
                    field => field.name.includes(name)
                      && field.name.includes('lasttree')
                  );

                  if (themeValue?.value) {
                    subThemesOptions = getSubThemeOptions(themeValue.value);
                  }

                  if (subThemeValue?.value) {
                    const subThemeTreeData = getSubThemeTreeData(subThemeValue.value);
                    lastTreeOptions = selectableTree(subThemeTreeData);
                  }

                  if (!lodash.isUndefined(subThemeValue)
                    && !lodash.isUndefined(subThemeValue.value)
                    && lodash.isEmpty(lastTreeOptions)) {
                    letters = getSubThemeLetters(subThemeValue.value);
                  }

                  if (!lodash.isUndefined(lastTreeValue)
                    && !lodash.isUndefined(lastTreeValue.value)
                    && !lastTreeValue.value.search('toplevel') > -1) {
                    letters = getSubThemeLetters(lastTreeValue.value);
                  }

                  return (
                    <>
                      <Divider />
                      <Card
                        title={`Ingrédient ${name + 1}`}
                        style={{ marginBottom: '60px' }}
                        extra={getLettersCode(letters)}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'ingredient']}
                          fieldKey={[fieldKey, 'ingredient']}
                          key={[fieldKey, 'ingredient']}
                          label="Nom de votre ingrédient (optionnel)"
                          labelAlign="left"
                        >
                          <Input
                            style={{ width: '100%' }}
                            placeholder="Ingrédient"
                            size="large"
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'theme']}
                          fieldKey={[fieldKey, 'theme']}
                          key={[fieldKey, 'theme']}
                          label="Description des denrées alimentaires"
                          labelAlign="left"
                          rules={[
                            {
                              required: true,
                              message: 'Champs requis',
                            },
                          ]}
                        >
                          <TreeSelect
                            allowClear
                            style={{ width: '100%' }}
                            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                            treeData={getThemesTreeData()}
                            placeholder="Famille"
                            treeDefaultExpandAll
                            size="large"
                          />
                        </Form.Item>
                        {themeValue && themeValue.value !== undefined && (
                          <Form.Item
                            shouldUpdate={
                              (prevValues, curValues) => {
                                if (prevValues.ingredients[name]) {
                                  if ((prevValues.ingredients[name] && prevValues.ingredients[name].theme)
                                    && (curValues.ingredients[name] && curValues.ingredients[name].theme)
                                    && (prevValues.ingredients[name].theme !== curValues.ingredients[name].theme)) {
                                    if (subThemeValue) {
                                      form.resetFields([subThemeValue.name]);
                                    }
                                    if (lastTreeValue) {
                                      form.resetFields([lastTreeValue.name]);
                                    }
                                  }
                                }
                              }
                            }
                            {...restField}
                            name={[name, 'subtheme']}
                            fieldKey={[fieldKey, 'subtheme']}
                            key={[fieldKey, 'subtheme']}
                            labelAlign="left"
                            rules={[
                              {
                                required: true,
                                message: 'Champs requis',
                              },
                            ]}
                          >
                            <TreeSelect
                              allowClear
                              style={{ width: '100%' }}
                              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                              treeData={subThemesOptions}
                              placeholder="Sous famille"
                              treeDefaultExpandAll
                              size="large"
                            />
                          </Form.Item>
                        )}
                        {subThemeValue && subThemeValue.value !== undefined && !lodash.isEmpty(lastTreeOptions) && (
                          <Form.Item
                            shouldUpdate={
                              (prevValues, curValues) => {
                                if (prevValues.ingredients[name]) {
                                  if (prevValues.ingredients[name].subtheme !== curValues.ingredients[name].subtheme) {
                                    if (lastTreeValue) {
                                      form.resetFields([lastTreeValue.name]);
                                    }
                                  }
                                }
                              }
                            }
                            {...restField}
                            name={[name, 'lasttree']}
                            fieldKey={[fieldKey, 'lasttree']}
                            key={[fieldKey, 'lasttree']}
                            labelAlign="left"
                            rules={[
                              {
                                required: true,
                                message: 'Champs requis',
                              },
                              () => ({
                                validator(_, value) {
                                  if (value.search('toplevel') > -1) {
                                    return Promise.reject(new Error('Veuillez choisir une sous famille'));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <TreeSelect
                              allowClear
                              style={{ width: '100%' }}
                              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                              treeData={lastTreeOptions}
                              placeholder="Sous famille"
                              treeDefaultExpandAll
                              size="large"
                            />
                          </Form.Item>
                        )}
                        {letters && checkForInputPhNeeded(letters) && (
                          <Form.Item
                            shouldUpdate
                            {...restField}
                            name={[name, 'condition']}
                            fieldKey={[fieldKey, 'condition']}
                            key={[fieldKey, 'condition']}
                            label="PH du produit (optionnel)"
                            labelAlign="left"
                          >
                            <Input
                              style={{ width: '100%' }}
                              placeholder="PH"
                              size="large"
                            />
                          </Form.Item>
                        )}
                        {letters && checkForBoldTouch(letters) && (
                          <Form.Item
                            shouldUpdate
                            {...restField}
                            name={[name, 'bold']}
                            fieldKey={[fieldKey, 'bold']}
                            key={[fieldKey, 'bold']}
                            label="Un essai approprié a t-il démontré qu'aucun contact gras ne s'établit avec la matière
                          plastique en contact avec les denrées alimentaires?"
                            labelAlign="left"
                          >
                            <Select>
                              <Option value="yes">Oui</Option>
                              <Option value="no">Non</Option>
                            </Select>
                          </Form.Item>
                        )}
                        {letters && (
                          <Form.Item
                            shouldUpdate
                            {...restField}
                            name={[name, 'code']}
                            fieldKey={[fieldKey, 'code']}
                            key={[fieldKey, 'code']}
                            initialValue={letters.items}
                            hidden
                          >
                            <Input size="large" />
                          </Form.Item>
                        )}
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ marginBottom: '10px' }} />
                        {' '}
                        Supprimer l&apos;ingrédient
                      </Card>
                    </>
                  );
                })}
                <Form.Item
                  style={{ marginTop: '10px' }}
                >
                  <Button
                    type="primary"
                    onClick={() => {
                      add();
                    }}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginBottom: '150px' }}
                  >
                    Ajouter un ingrédient
                  </Button>
                  <Form.ErrorList errors={errors} />
                  <div ref={endRef} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </InnerFormWrapper>
        <Form.Item style={{ textAlign: 'center' }}>
          <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
            <Button type="primary" htmlType="submit" icon={<CheckOutlined />}>
              Valider ma recette
            </Button>
            <Button onClick={() => setShow(false)} icon={<StopOutlined />}>
              Annuler
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </>
  );
};

const RecipeForm = (props) => {
  const { themes, subThemes, setShow } = props;
  const [fields, setFields] = useState([
    {
      name: ['ingredients'],
      value: '',
    },
  ]);

  // eslint-disable-next-line no-unused-vars
  const onFinish = values => {
    const { history } = props;
    history.replace({
      pathname: '/recipe_result',
      state: { recipe: values }
    });
  };

  return (
    <FormWrapper>
      <CustomizedForm
        setShow={setShow}
        themes={themes}
        subThemes={subThemes}
        onFinish={onFinish}
        infields={fields}
        onChange={(newFields) => {
          setFields(newFields);
        }}
      />
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  margin-bottom: 80px;
`;

const InnerFormWrapper = styled.div`
  max-height: 60vh;
  overflow: scroll;
`;

CustomizedForm.propTypes = {
  themes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    ref: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  subThemes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    ref: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })),
  onFinish: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  infields: PropTypes.arrayOf(PropTypes.shape({})),
  setShow: PropTypes.func.isRequired,
};

RecipeForm.propTypes = {
  themes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    ref: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  subThemes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    ref: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })),
  history: PropTypes.shape({
    replace: PropTypes.func
  }),
  setShow: PropTypes.func.isRequired,
};

export default RecipeForm;
